import { storage } from '@/firebase';
import { ref as firebaseRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import SharedService from '../services/shared';
import { Alert } from '../plugins/alert';
import { useAuthStore } from '../store/shared/auth-store';
import imageCompression from 'browser-image-compression';
import { fixOggOpusGranules } from './ogg-opus-granule';

export const uploadToFireStore = (file, path) => {
	return new Promise((resolve, reject) => {
		try {
			const storageRef = firebaseRef(storage, path);
			const uploadImage = uploadBytesResumable(storageRef, file);

			uploadImage.on(
				'state_changed',
				(snapshot) => {},
				(error) => {
					console.error('Error uploading file:', error);
					reject(error);
				},
				async () => {
					// Upload completed, get download URL
					const filePath = await getDownloadURL(uploadImage.snapshot.ref);
					return resolve({ filePath });
				},
			);
		} catch (err) {
			reject(err);
		}
	});
};

export const getFirestorePath = ({ file, ext }) => {
	const authStore = useAuthStore();
	const now = new Date();
	const fileRef = `${now.getTime() + file.name}`;

	if (['jpg', 'png', 'jpeg', 'svg', 'webp', 'gif'].includes(ext)) {
		return `tenants/${authStore.tenantId}/images/${fileRef}`;
	}

	if (['mp4'].includes(ext)) {
		return `tenants/${authStore.tenantId}/videos/${fileRef}`;
	}

	return `tenants/${authStore.tenantId}/documents/${fileRef}`;
};

export const uploadToS3 = async ({ file }) => {
	const formData = new FormData();
	formData.append('file', file);
	try {
		const data = await SharedService.uploadTempFile(formData);

		const filePath = data.result?.url;
		const shortPath = data.result?.path;

		return {
			filePath,
			shortPath,
		};
	} catch (err) {
		const msg = err?.response?.data?.message;
		Alert.error(msg || t('Error Msg'));
		console.error(err);
		throw err;
	}
};

export const getFileExt = (fileName) => {
	const s = fileName.split('.');
	return s[s.length - 1];
};

export const isFileValid = (fileName, allowedExts) => {
	if (allowedExts.length === 0) return true;
	return allowedExts.includes(getFileExt(fileName));
};

export const downloadBlob = (blob, fileName = 'file') => {
	const url = URL.createObjectURL(blob);
	console.log({ url });

	const aTag = document.createElement('a');
	aTag.href = url;

	aTag.setAttribute('download', fileName);
	document.body.appendChild(aTag);
	aTag.click();
};

export const base64ToBlob = (base64Data, mimeType) => {
	const byteString = atob(base64Data);
	const arrayBuffer = new ArrayBuffer(byteString.length);
	const int8Array = new Uint8Array(arrayBuffer);
	for (let i = 0; i < byteString.length; i++) {
		int8Array[i] = byteString.charCodeAt(i);
	}
	return new Blob([arrayBuffer], { type: mimeType });
};
export const convertWebMToOggOpus = async (webMBlob) => {
	const ffmpeg = new FFmpeg({ log: true });
	await ffmpeg.load();

	// Prepare the filenames for input and output
	const inputFileName = 'input.webm';
	const outputFileName = 'output.ogg';

	// Write the input WebM blob to FFmpeg's virtual file system
	await ffmpeg.writeFile(inputFileName, await fetchFile(webMBlob));

	// Execute the conversion command using the Opus codec
	await ffmpeg.exec(['-i', inputFileName, '-c:a', 'libopus', outputFileName]);

	// Read the converted OGG file from FFmpeg's file system
	const data = await ffmpeg.readFile(outputFileName);

	// The bundled ffmpeg core mistimes Ogg granule positions for what MediaRecorder now
	// produces, and WhatsApp rejects the result with error 131053. Repair them before upload.
	const oggBlob = new Blob([fixOggOpusGranules(data)], { type: 'audio/ogg; codecs=opus' });

	return oggBlob;
};

export const convertWebMToAac = async (webMBlob) => {
	const ffmpeg = new FFmpeg({ log: true });
	await ffmpeg.load();

	// Prepare the filenames for input and output
	const inputFileName = 'input.webm';
	const outputFileName = 'output.aac';

	// Write the input WebM blob to FFmpeg's virtual file system
	await ffmpeg.writeFile(inputFileName, await fetchFile(webMBlob));

	// Execute the conversion command using the AAC codec
	await ffmpeg.exec(['-i', inputFileName, '-c:a', 'aac', outputFileName]);

	// Read the converted AAC file from FFmpeg's file system
	const data = await ffmpeg.readFile(outputFileName);

	// Convert the Uint8Array to a Blob with the correct MIME type
	const aacBlob = new Blob([data.buffer], { type: 'audio/aac' });

	return aacBlob;
};

export const fetchAudioMetadata = (url) => {
	return new Promise((resolve, reject) => {
		const audio = new Audio();
		audio.addEventListener('loadedmetadata', () => {
			resolve(Math.floor(audio.duration));
		});

		audio.addEventListener('error', () => {
			reject(0);
		});

		audio.src = url;
	});
};

export const compressImageFile = async (file, options = {}) => {
	const defaultOptions = {
		maxSizeMB: 1,
		maxWidthOrHeight: 1920,
		useWebWorker: true,
	};
	const merged = { ...defaultOptions, ...options };
	const compressedBlob = await imageCompression(file, merged);
	const name = file.name;
	return new File([compressedBlob], name, { type: compressedBlob.type || file.type });
};

export const compressVideoFile = async (file, options = {}) => {
	const ffmpeg = new FFmpeg({ log: false, ...(options.ffmpegConfig || {}) });
	await ffmpeg.load();

	const inputExt = (file.name.split('.').pop() || 'mp4').toLowerCase();
	const inputName = `input.${inputExt}`;
	const outputName = 'output.mp4';

	await ffmpeg.writeFile(inputName, await fetchFile(file));

	const maxHeight = options.maxHeight || 1080;
	const crf = options.crf ?? 28;
	const preset = options.preset || 'veryfast';
	const videoCodec = options.videoCodec || 'libx264';
	const audioCodec = options.audioCodec || 'aac';
	const audioBitrate = options.audioBitrate || '128k';

	const args = [
		'-i',
		inputName,
		'-vf',
		`scale=-2:${maxHeight}`,
		'-c:v',
		videoCodec,
		'-preset',
		preset,
		'-crf',
		String(crf),
		'-c:a',
		audioCodec,
		'-b:a',
		audioBitrate,
		'-movflags',
		'+faststart',
		outputName,
	];

	await ffmpeg.exec(args);

	const data = await ffmpeg.readFile(outputName);
	const blob = new Blob([data.buffer], { type: 'video/mp4' });
	const baseName = file.name.replace(/\.[^/.]+$/, '');
	const newName = `${baseName}.mp4`;
	return new File([blob], newName, { type: 'video/mp4' });
};
